import React, { useState } from "react";
import StatusBadge from "@/components/StatusBadge";
import LogoUploader from "@/components/LogoUploader";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { School, Mail, Phone, Plus, Sparkles, CheckCircle2, Pencil, RefreshCcw, Globe, Copy, ExternalLink, Eye, Upload, X } from "lucide-react";
import EntityDetailDialog from "@/components/EntityDetailDialog";
import { useAuth } from "@/contexts/AuthContext";
import { compressImage } from "@/lib/imageCompress";

export default function SchoolProfilePage() {
  const { user } = useAuth();
  const { schools, selectedSchoolId, setSelectedSchoolId, refresh, loading } = useBranch();
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null); // school doc when editing/resubmitting
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", contact_email: "", contact_phone: "", description: "",
    payment_details_text: "", print_tagline: "",
  });
  // Live availability check for the slug derived from the school name.
  const [nameCheck, setNameCheck] = useState({ status: "idle", cleaned: "", reason: "" });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  // Mirror the backend `slugify()` so we can preview the URL client-side.
  const previewSlug = (n) => (n || "")
    .toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  React.useEffect(() => {
    if (!openForm || editing) { setNameCheck({ status: "idle", cleaned: "", reason: "" }); return; }
    const cleaned = previewSlug(form.name);
    if (!cleaned) { setNameCheck({ status: "idle", cleaned: "", reason: "" }); return; }
    if (cleaned.length < 3) { setNameCheck({ status: "invalid", cleaned, reason: "Name is too short" }); return; }
    setNameCheck((c) => ({ ...c, status: "checking", cleaned }));
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/schools/slug-check", { params: { slug: cleaned } });
        if (data.available) setNameCheck({ status: "available", cleaned: data.cleaned, reason: "" });
        else setNameCheck({ status: "taken", cleaned: data.cleaned, reason: data.reason });
      } catch (err) {
        setNameCheck({ status: "invalid", cleaned, reason: formatApiError(err) });
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [form.name, openForm, editing]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", contact_email: "", contact_phone: "", description: "", payment_details_text: "", print_tagline: "" });
    setOpenForm(true);
  };

  const openEdit = (school) => {
    setEditing(school);
    setForm({
      name: school.name || "",
      contact_email: school.contact_email || "",
      contact_phone: school.contact_phone || "",
      description: school.description || "",
      payment_details_text: school.payment_details_text || "",
      print_tagline: school.print_tagline || "",
      report_card_header_photo_base64: school.report_card_header_photo_base64 || "",
    });
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/schools/${editing.id}`, form);
        toast.success(editing.status === "rejected" ? "Resubmitted for approval ✨" : "School updated");
      } else {
        await api.post("/schools/register", form);
        toast.success(`${form.name} submitted for approval! 🎉`);
      }
      setOpenForm(false);
      setEditing(null);
      setForm({ name: "", contact_email: "", contact_phone: "", description: "", payment_details_text: "", print_tagline: "" });
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">My Schools</h1>
          <p className="text-brand-inkMuted mt-1">All schools you own. Register as many as you need.</p>
        </div>
        <Button
          onClick={openCreate}
          className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white shadow-sm"
          data-testid="register-school-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Register school
        </Button>
      </div>

      {loading ? (
        <div className="text-brand-inkMuted font-heading">Loading...</div>
      ) : schools.length === 0 ? (
        <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-10 text-center max-w-xl" data-testid="no-schools">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
            <Sparkles className="w-7 h-7 text-brand-purple" />
          </div>
          <p className="font-heading text-lg font-semibold text-foreground">Register your first school</p>
          <p className="text-brand-inkMuted mt-1">Our Super Admins will review it and approve shortly.</p>
          <Button
            onClick={openCreate}
            className="mt-5 rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white"
          >
            <Plus className="w-4 h-4 mr-1" /> Register school
          </Button>
        </section>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="schools-grid">
          {schools.map((s) => {
            const isSelected = s.id === selectedSchoolId;
            return (
              <div
                key={s.id}
                onClick={() => s.status === "approved" && setSelectedSchoolId(s.id)}
                className={`bg-white rounded-3xl border shadow-sm p-6 transition-all duration-300 ${
                  s.status === "approved" ? "hover:-translate-y-1 hover:shadow-md cursor-pointer" : ""
                } ${isSelected ? "border-brand-purple ring-2 ring-brand-purple/30" : "border-brand-purple/10"}`}
                data-testid={`school-card-${s.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <LogoUploader
                    kind="schools" id={s.id} logo={s.logo_base64}
                    size="md" editable={true}
                    onUpdated={() => refresh()}
                  />
                  <StatusBadge status={s.status} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground">{s.name}</h3>
                <div className="text-sm text-brand-inkMuted mt-2 space-y-1">
                  <p className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{s.contact_email}</p>
                  <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{s.contact_phone}</p>
                </div>
                {s.status === "rejected" && s.rejection_reason && (
                  <div className="mt-3 rounded-xl bg-[#FFEBEE] text-[#C62828] px-3 py-2 text-xs">
                    {s.rejection_reason}
                  </div>
                )}
                {(s.status === "rejected" || s.status === "pending") && (
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); openEdit(s); }}
                      className={`rounded-full text-xs h-8 px-3 ${
                        s.status === "rejected"
                          ? "border-[#C62828]/40 text-[#C62828] hover:bg-[#FFEBEE]"
                          : "border-brand-purple/30 text-brand-purple hover:bg-brand-purpleLight"
                      }`}
                      data-testid={`school-${s.status === "rejected" ? "resubmit" : "edit"}-btn-${s.id}`}
                    >
                      {s.status === "rejected" ? (
                        <><RefreshCcw className="w-3.5 h-3.5 mr-1" /> Edit & Resubmit</>
                      ) : (
                        <><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</>
                      )}
                    </Button>
                  </div>
                )}
                {s.status === "approved" && s.slug && (
                  <PublicLinkBlock school={s} refresh={refresh} />
                )}
                {s.status === "approved" && (
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={(e) => { e.stopPropagation(); setDetailId(s.id); setDetailOpen(true); }}
                      className="rounded-full text-xs h-8 px-3 border-[#BFDBFE] text-[#1D4ED8] hover:bg-[#EFF6FF]"
                      data-testid={`school-view-${s.id}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> View details
                    </Button>
                  </div>
                )}
                {isSelected && s.status === "approved" && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-purpleHover">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Currently selected
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={openForm} onOpenChange={(o) => { setOpenForm(o); if (!o) setEditing(null); }}>
        <DialogContent className="rounded-3xl border-brand-purple/20 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="register-school-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              {editing ? (
                editing.status === "rejected"
                  ? (<><RefreshCcw className="w-5 h-5 text-[#C62828]" /> Edit & Resubmit</>)
                  : (<><Pencil className="w-5 h-5 text-brand-purple" /> Edit School</>)
              ) : (
                <><Sparkles className="w-5 h-5 text-brand-purple" /> Register a school</>
              )}
            </DialogTitle>
            <DialogDescription>
              {editing?.status === "rejected"
                ? "Update the details and the school will go back to pending for super-admin re-review."
                : "Our Super Admins will review and approve your school shortly."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="school-form">
            <div className="sm:col-span-2">
              <Label className="font-heading">School name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                placeholder="Sunshine Kids Academy" data-testid="school-name-input" />
              {/* Live URL preview + availability (only on new registration) */}
              {!editing && nameCheck.cleaned && (
                <div
                  className={`mt-1.5 flex items-center gap-1.5 text-xs font-medium ${
                    nameCheck.status === "taken" || nameCheck.status === "invalid"
                      ? "text-[#C62828]"
                      : nameCheck.status === "available"
                        ? "text-[#15803D]"
                        : "text-slate-500"
                  }`}
                  data-testid="reg-slug-status"
                >
                  <Globe className="w-3 h-3 shrink-0" />
                  <span className="font-mono truncate">/school/{nameCheck.cleaned}</span>
                  <span className="opacity-80">·</span>
                  <span>
                    {nameCheck.status === "checking" && "checking…"}
                    {nameCheck.status === "available" && "available"}
                    {nameCheck.status === "taken" && "already taken — try a different name"}
                    {nameCheck.status === "invalid" && (nameCheck.reason || "invalid")}
                  </span>
                </div>
              )}
            </div>
            <div>
              <Label className="font-heading">Contact email</Label>
              <Input required type="email" value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                data-testid="school-email-input" />
            </div>
            <div>
              <Label className="font-heading">Contact phone</Label>
              <Input required value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                data-testid="school-phone-input" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-heading">About your school (optional)</Label>
              <Textarea value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="rounded-2xl mt-1.5 border-brand-purple/20" rows={3}
                data-testid="school-description-input" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-heading">Print Tagline (optional)</Label>
              <Input value={form.print_tagline || ""}
                onChange={(e) => setForm({ ...form, print_tagline: e.target.value })}
                placeholder='e.g. "A Progressive Approach Playschool"'
                maxLength={200}
                className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                data-testid="school-print-tagline-input" />
              <p className="text-[11px] text-brand-inkMuted mt-1">Shown under the school name on Statement of Account printouts.</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="font-heading">Payment / Bank Details (for Statement of Account)</Label>
              <Textarea value={form.payment_details_text || ""}
                onChange={(e) => setForm({ ...form, payment_details_text: e.target.value })}
                placeholder={"BDO - 011108001607 (Mylene E. Malate)\nGCash - 09179832931 (Mylene E. Malate)\nPayMaya - 09179832931 (Mylene E. Malate)"}
                maxLength={2000}
                className="rounded-2xl mt-1.5 border-brand-purple/20 font-mono text-sm" rows={4}
                data-testid="school-payment-details-input" />
              <p className="text-[11px] text-brand-inkMuted mt-1">One line per account. Rendered as-is on the SOA footer.</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="font-heading">Report Card Header Photo (optional)</Label>
              <ReportCardHeaderUploader
                value={form.report_card_header_photo_base64 || ""}
                onChange={(v) => setForm({ ...form, report_card_header_photo_base64: v })}
              />
              <p className="text-[11px] text-brand-inkMuted mt-1">
                Wide banner printed at the top of every Report Card. Replaces the school logo + name block. Recommend a landscape image (e.g. 1600×300px) — it scales to the paper width.
              </p>
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpenForm(false)}
                className="rounded-full border-brand-purple/30">
                Cancel
              </Button>
              <Button type="submit" disabled={busy || (!editing && nameCheck.status === "taken")}
                className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="school-submit-btn">
                {busy
                  ? "Submitting..."
                  : editing
                    ? (editing.status === "rejected" ? "Resubmit for approval" : "Save changes")
                    : "Submit for approval"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <EntityDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        kind="school"
        id={detailId}
        role={user?.role}
        onEdit={(s) => { setDetailOpen(false); openEdit(s); }}
        onChanged={refresh}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Public landing page block — visible on approved school cards               */
/* -------------------------------------------------------------------------- */
function PublicLinkBlock({ school, refresh }) {
  const [editing, setEditing] = useState(false);
  const [slug, setSlug] = useState(school.slug);
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState({ status: "idle", reason: "", cleaned: "" });
  // Build the front-end URL using window.location so it works both in preview
  // and production environments.
  const publicUrl = `${window.location.origin}/school/${school.slug}`;

  // ----- Debounced live availability check -----
  React.useEffect(() => {
    if (!editing) return;
    const trimmed = (slug || "").trim();
    // If unchanged from the current slug, no need to check.
    if (trimmed === school.slug) { setCheck({ status: "idle", reason: "", cleaned: trimmed }); return; }
    if (trimmed.length < 3) { setCheck({ status: "invalid", reason: "Slug must be at least 3 characters", cleaned: "" }); return; }
    setCheck((c) => ({ ...c, status: "checking" }));
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(`/schools/slug-check`, { params: { slug: trimmed, exclude_id: school.id } });
        if (data.available) setCheck({ status: "available", reason: "", cleaned: data.cleaned });
        else setCheck({ status: "taken", reason: data.reason || "URL is already taken", cleaned: data.cleaned });
      } catch (err) {
        setCheck({ status: "invalid", reason: formatApiError(err), cleaned: "" });
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [slug, editing, school.id, school.slug]);

  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Public link copied");
    } catch {
      toast.error("Couldn't copy automatically — long-press to copy");
    }
  };

  // Save is disabled when the slug is taken / invalid / still being checked.
  const trimmed = (slug || "").trim();
  const unchanged = trimmed === school.slug;
  const saveDisabled = busy
    || trimmed.length < 3
    || (!unchanged && (check.status === "taken" || check.status === "invalid" || check.status === "checking"));

  const save = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (saveDisabled) return;
    if (trimmed.length < 3) {
      toast.error("Slug must be at least 3 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/schools/${school.id}`, { slug: trimmed });
      toast.success(`Public link updated · /school/${data.slug}`);
      setEditing(false);
      refresh?.();
    } catch (err) {
      // Surface 409 nicely
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="mt-4 rounded-2xl border border-[#2563EB]/15 bg-[#EFF6FF] p-3"
      onClick={(e) => e.stopPropagation()}
      data-testid={`public-link-block-${school.id}`}
    >
      <div className="flex items-center gap-2 text-[11px] font-heading font-semibold text-[#1D4ED8] uppercase tracking-wider mb-1.5">
        <Globe className="w-3.5 h-3.5" /> Your Public Page
      </div>
      {!editing ? (
        <>
          <div className="flex items-center gap-2 text-xs text-foreground font-body break-all">
            <span className="text-slate-500">/school/</span>
            <span className="font-heading font-semibold" data-testid={`public-slug-${school.id}`}>{school.slug}</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <a href={publicUrl} target="_blank" rel="noreferrer">
              <Button type="button" size="sm" className="rounded-full h-8 px-3 text-xs bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white shadow-sm"
                data-testid={`open-public-page-${school.id}`}>
                <ExternalLink className="w-3 h-3 mr-1" /> Open
              </Button>
            </a>
            <Button type="button" size="sm" variant="outline" onClick={copy}
              className="rounded-full h-8 px-3 text-xs border-[#2563EB]/30 text-[#1D4ED8] hover:bg-white"
              data-testid={`copy-public-link-${school.id}`}>
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setEditing(true); }}
              className="rounded-full h-8 px-3 text-xs border-[#2563EB]/30 text-[#1D4ED8] hover:bg-white"
              data-testid={`edit-slug-${school.id}`}>
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>
          </div>
        </>
      ) : (
        <form onSubmit={save} className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 shrink-0">/school/</span>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              autoFocus
              className={`h-9 rounded-xl bg-white text-sm ${
                check.status === "taken" || check.status === "invalid"
                  ? "border-[#C62828]/60 focus-visible:ring-[#C62828]/40"
                  : check.status === "available"
                    ? "border-[#15803D]/60 focus-visible:ring-[#15803D]/40"
                    : "border-[#2563EB]/20"
              }`}
              data-testid={`slug-input-${school.id}`}
              placeholder="my-school"
            />
          </div>
          {/* Inline availability feedback */}
          {!unchanged && (
            <p
              className={`text-[11px] leading-tight font-medium ${
                check.status === "taken" || check.status === "invalid"
                  ? "text-[#C62828]"
                  : check.status === "available"
                    ? "text-[#15803D]"
                    : "text-slate-500"
              }`}
              data-testid={`slug-status-${school.id}`}
            >
              {check.status === "checking" && "Checking availability…"}
              {check.status === "available" && `✓ /school/${check.cleaned} is available`}
              {check.status === "taken" && check.reason}
              {check.status === "invalid" && check.reason}
              {check.status === "idle" && "Enter a new URL"}
            </p>
          )}
          <p className="text-[10px] text-slate-500 leading-tight">
            Lowercase letters, numbers and hyphens only. We'll clean it up automatically.
          </p>
          <div className="flex gap-1.5">
            <Button type="submit" size="sm" disabled={saveDisabled}
              className="rounded-full h-8 px-3 text-xs bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`slug-save-${school.id}`}>
              {busy ? "Saving..." : "Save"}
            </Button>
            <Button type="button" size="sm" variant="outline"
              onClick={(e) => { e.stopPropagation(); setEditing(false); setSlug(school.slug); }}
              className="rounded-full h-8 px-3 text-xs border-slate-200">
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}


/**
 * Wide banner uploader for the Report Card header photo.
 * Stores the compressed base64 in the parent form; PATCH happens on Save.
 */
function ReportCardHeaderUploader({ value, onChange }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file (PNG, JPG, etc.)");
      return;
    }
    setBusy(true);
    try {
      // Wide banner — allow up to 800KB and 1800px on longest edge so
      // print quality stays crisp across the paper width.
      const compressed = await compressImage(file, { maxKB: 800, maxDim: 1800 });
      onChange(compressed);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-1.5 rounded-2xl border border-brand-purple/20 bg-white p-3" data-testid="school-header-photo-uploader">
      {value ? (
        <div className="relative">
          <img
            src={value}
            alt="Report Card header preview"
            className="w-full h-auto rounded-xl object-contain bg-brand-purpleLight/20"
            style={{ maxHeight: "180px" }}
          />
          <div className="mt-2 flex gap-2">
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => inputRef.current?.click()} disabled={busy}
              className="rounded-full"
              data-testid="school-header-photo-replace"
            >
              <Upload className="w-3.5 h-3.5 mr-1" />
              {busy ? "Uploading..." : "Replace"}
            </Button>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => onChange("")}
              className="rounded-full text-[#C62828] hover:bg-[#FFEBEE]"
              data-testid="school-header-photo-remove"
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-full py-8 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-brand-purple/30 hover:bg-brand-purpleLight/30 transition"
          data-testid="school-header-photo-upload"
        >
          <Upload className="w-6 h-6 text-brand-purple" />
          <span className="text-sm text-foreground font-heading font-semibold">
            {busy ? "Uploading..." : "Click to upload a header banner"}
          </span>
          <span className="text-xs text-brand-inkMuted">PNG or JPG · Landscape recommended</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
      />
    </div>
  );
}
